import { useEffect } from "react";

import * as CookieConsent from "vanilla-cookieconsent";

import { forgetTracking } from "./analytics";
import { hasTrackingConsent, onConsentChange } from "./consent";
import { cookieConsentConfig } from "./cookieConsentConfig";

import "vanilla-cookieconsent/dist/cookieconsent.css";

export const isSnap = typeof navigator !== "undefined" && navigator.userAgent === "ReactSnap";
export const isSnapDev = typeof navigator !== "undefined" && navigator.userAgent === "ReactSnap_Dev";

const OPENREPLAY_SRC = "https://static.openreplay.com/latest/openreplay.js";

/**
 * Injects the OpenReplay tracker. Called ONLY once analytics consent exists.
 *
 * This is OpenReplay's published bootstrap snippet, written out as ordinary
 * module code instead of injected as an inline `<script>`. It does exactly what
 * the snippet did — publish a queue on `window.OpenReplay` that buffers calls,
 * then load the real tracker, which drains it — but it runs as part of our own
 * bundle, so a `script-src` without `'unsafe-inline'` allows it. The inline
 * version was the site's only script-src violation (found 2026-07-28 by the
 * report-only CSP), and keeping it would have meant either weakening the policy
 * for every script on the site or pinning a hash that changes whenever
 * OpenReplay edits its snippet.
 */
const startOpenReplay = () => {
  const initOpts = {
    projectKey: "nMk6pMLWB97UY1guQVKQ",
    defaultInputMode: 2,
    obscureTextNumbers: false,
    obscureTextEmails: true,
  };
  const startOpts = { userID: "" };

  // The queue's shape is the tracker's contract, not ours: it reads
  // [startOpts, <reserved>, initOpts, [mode, startOpts]] and then replays
  // whatever was pushed. Do not tidy the positions.
  const queue: any = [startOpts, undefined, initOpts, [0, startOpts]];
  queue.start = () => queue.push([0]);
  queue.stop = () => queue.push([1]);
  queue.setUserID = (id: string) => queue.push([2, id]);
  queue.setUserAnonymousID = (id: string) => queue.push([3, id]);
  queue.setMetadata = (k: string, v: string) => queue.push([4, k, v]);
  queue.event = (k: string, p?: unknown, i?: unknown) => queue.push([5, k, p, i]);
  queue.issue = (k: string, p?: unknown) => queue.push([6, k, p]);
  queue.isActive = () => false;
  queue.getSessionToken = () => undefined;
  (window as any).OpenReplay = queue;

  const script = document.createElement("script");
  // The id stays on whichever element is the tracker — the consent E2E checks
  // for it to prove recording really did start (and really did not).
  script.id = "openreplay-tracker";
  script.src = OPENREPLAY_SRC;
  script.async = true;
  document.head.appendChild(script);
};

export const ExternalScriptsLoader = () => {
  useEffect(() => {
    if (isSnap || isSnapDev) return;

    const w = window as any;

    // The banner itself is strictly necessary — it exists to record the visitor's
    // own choice — so it runs unconditionally. Everything else waits on its answer.
    void CookieConsent.run(cookieConsentConfig);

    // OpenReplay records real sessions → NOT strictly necessary → only on an
    // express accept. The snippet auto-starts the moment it lands, so the gate has
    // to wrap the injection itself rather than a later .start() call.
    let started = false;

    // `isInitial` separates page load from an actual decision: on a fresh load with
    // no consent there is nothing to undo, and without this every declining visitor
    // would fire a pointless /analytics/forget on every page view.
    const apply = (isInitial: boolean) => {
      if (hasTrackingConsent()) {
        if (started) return;
        started = true;
        startOpenReplay();
        return;
      }

      if (isInitial) return;

      // Withdrawal must be as effective as the accept was, and none of it is
      // automatic: our beacons stop themselves (they re-check per call), but
      // OpenReplay keeps recording and the device keeps its state until told.
      forgetTracking();
      if (!started) return;
      const stopped = (() => {
        try {
          if (typeof w.OpenReplay?.stop === "function") {
            w.OpenReplay.stop();
            return true;
          }
        } catch {
          /* fall through to the reload */
        }
        return false;
      })();
      started = false;
      // If we can't prove it stopped, reload — the tracker is simply never
      // injected on the next load. Blunt, but withdrawal is a right.
      if (!stopped) window.location.reload();
    };

    apply(true);
    const unsubscribe = onConsentChange(() => apply(false));

    return () => unsubscribe();
  }, []);

  return null;
};
